import tr from './tr.json';
import en from './en.json';
import { debugLog, debugWarn } from '@shared/utils/Logger';

type LocaleData = typeof tr;
type LocaleKey = 'tr' | 'en';

const locales: Record<LocaleKey, LocaleData> = {
  tr,
  en,
};

// Current locale (default: Turkish)
let currentLocale: LocaleKey = 'tr';

// Storage key for persisting language preference
const STORAGE_KEY = 'arpg_language';

// Initialize locale from storage or browser preference
function initLocale(): void {
  // Try to load from localStorage
  const stored = localStorage.getItem(STORAGE_KEY) as LocaleKey | null;
  if (stored && locales[stored]) {
    currentLocale = stored;
    return;
  }

  // Try browser language
  const browserLang = navigator.language.split('-')[0];
  if (browserLang === 'en') {
    currentLocale = 'en';
  } else {
    currentLocale = 'tr'; // Default to Turkish
  }
}

// Initialize on module load
initLocale();

/**
 * Get a translated string by key path
 * @param key - Dot-separated key path (e.g., 'ui.play', 'combat.damage')
 * @param params - Optional parameters for interpolation (e.g., { amount: 100 })
 * @returns Translated string
 */
export function t(key: string, params?: Record<string, string | number>): string {
  const keys = key.split('.');
  let value: unknown = locales[currentLocale];

  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = (value as Record<string, unknown>)[k];
    } else {
      // Fallback to key if not found
      debugWarn('i18n', `Missing translation: ${key}`);
      return key;
    }
  }

  if (typeof value !== 'string') {
    debugWarn('i18n', `Translation is not a string: ${key}`);
    return key;
  }

  // Interpolate parameters (e.g., {{amount}})
  if (params) {
    return value.replace(/\{\{(\w+)\}\}/g, (_, paramKey) => {
      return params[paramKey]?.toString() ?? `{{${paramKey}}}`;
    });
  }

  return value;
}

/**
 * Set the current locale
 * @param locale - Locale key ('tr' or 'en')
 */
export function setLocale(locale: LocaleKey): void {
  if (locales[locale]) {
    currentLocale = locale;
    localStorage.setItem(STORAGE_KEY, locale);
    debugLog('i18n', `Locale set to: ${locale}`);
  } else {
    debugWarn('i18n', `Unknown locale: ${locale}`);
  }
}

/**
 * Get the current locale
 * @returns Current locale key
 */
export function getCurrentLocale(): LocaleKey {
  return currentLocale;
}

/**
 * Get all available locales
 * @returns Array of available locale keys
 */
export function getAvailableLocales(): LocaleKey[] {
  return Object.keys(locales) as LocaleKey[];
}

/**
 * Check if a translation key exists
 * @param key - Dot-separated key path
 * @returns True if the key exists
 */
export function hasTranslation(key: string): boolean {
  const keys = key.split('.');
  let value: unknown = locales[currentLocale];

  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = (value as Record<string, unknown>)[k];
    } else {
      return false;
    }
  }

  return typeof value === 'string';
}

// Export types
export type { LocaleKey };
